import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { settingsService } from '../services/settingsService';
import { useAuth } from '../hooks/useAuth';
import { useSettings } from './SettingsContext';

const SecurityContext = createContext();

export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return context;
};

export const SecurityProvider = ({ children }) => {
  const { user } = useAuth();
  const { securitySettings, isLoading: settingsLoading } = useSettings();
  const [settings, setSettings] = useState({
    // Password Policy
    enforce_password_policy: true,
    min_password_length: 8,
    require_uppercase: true,
    require_lowercase: true,
    require_numbers: true,
    require_special_chars: true,
    password_expiry_days: 90,
    password_history_count: 5,
    prevent_common_passwords: true,
    
    // Account Security
    max_login_attempts: 5,
    account_lockout_duration: 15, // minutes
    require_email_verification: true,
    force_logout_on_password_change: true,
    
    // Session Security
    max_concurrent_sessions: 3,
    session_timeout_warning: 5, // minutes before timeout
    auto_logout_inactive: true,
    remember_me_duration: 30, // days
    
    // Two-Factor Authentication
    enable_2fa: false,
    require_2fa_for_admin: true,
    backup_codes_count: 10,
    
    // API Security
    api_rate_limiting: true,
    api_requests_per_minute: 100,
    require_api_key_rotation: false,
    api_key_expiry_days: 365,
    
    // Data Encryption
    encrypt_sensitive_data: true,
    encryption_key_rotation_days: 30,
    
    // Audit & Compliance
    log_all_actions: true,
    log_failed_attempts: true,
    retention_period_days: 365,
    compliance_mode: 'standard', // standard, strict, custom
    
    // IP Security
    enable_ip_whitelist: false,
    allowed_ip_ranges: [],
    block_suspicious_ips: true,
    
    // Browser Security
    secure_cookies: true,
    httponly_cookies: true,
    samesite_cookies: 'strict'
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [activeSessions, setActiveSessions] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [passwordStrength, setPasswordStrength] = useState(null);

  // Load security settings from centralized SettingsContext
  useEffect(() => {
    if (!settingsLoading && securitySettings) {
      setSettings(prev => ({
        ...prev,
        ...securitySettings
      }));
      setIsLoading(false);
    }
  }, [securitySettings, settingsLoading]);

  // Load sessions and events
  useEffect(() => {
    if (!isLoading) {
      loadActiveSessions();
      loadSecurityEvents();
    }
  }, [isLoading]);

  // Load active user sessions
  const loadActiveSessions = async () => {
    try {
      // TODO: Implement /api/security/sessions endpoint in backend
      // Temporarily disabled to prevent 404 errors
      // const response = await fetch('/api/security/sessions');
      // if (response.ok) {
      //   const sessions = await response.json();
      //   setActiveSessions(sessions);
      // }
    } catch (error) {
      logger.error('Failed to load active sessions:', error);
    }
  };

  // Load recent security events
  const loadSecurityEvents = async () => {
    try {
      // TODO: Implement /api/security/events endpoint in backend
      // Temporarily disabled to prevent 404 errors
      // const response = await fetch('/api/security/events?limit=50');
      // if (response.ok) {
      //   const events = await response.json();
      //   setSecurityEvents(events);
      // }
    } catch (error) {
      logger.error('Failed to load security events:', error);
    }
  };

  // Update security settings
  const updateSecuritySettings = async (newSettings) => {
    try {
      const updatedSettings = { ...settings, ...newSettings };
      setSettings(updatedSettings);
      
      // Save to backend
      await settingsService.updateSettingsBulk('security', updatedSettings);
    } catch (error) {
      logger.error('Failed to save security settings:', error);
      // Revert on error
      await loadSecuritySettings();
    }
  };

  // Password validation
  const validatePassword = useCallback((password) => {
    const errors = [];
    let score = 0;

    // Length check
    if (password.length < settings.min_password_length) {
      errors.push(`Password must be at least ${settings.min_password_length} characters`);
    } else {
      score += Math.min(password.length - settings.min_password_length + 1, 4);
    }

    // Character requirements
    if (settings.require_uppercase && !/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    } else if (settings.require_uppercase) {
      score += 1;
    }

    if (settings.require_lowercase && !/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    } else if (settings.require_lowercase) {
      score += 1;
    }

    if (settings.require_numbers && !/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    } else if (settings.require_numbers) {
      score += 1;
    }

    if (settings.require_special_chars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    } else if (settings.require_special_chars) {
      score += 1;
    }

    // Common password check
    if (settings.prevent_common_passwords && isCommonPassword(password)) {
      errors.push('This password is too common. Please choose a different one.');
    }

    // Calculate strength
    const maxScore = 8;
    const strengthPercentage = Math.min((score / maxScore) * 100, 100);
    
    let strength = 'weak';
    if (strengthPercentage >= 80) strength = 'strong';
    else if (strengthPercentage >= 60) strength = 'medium';
    else if (strengthPercentage >= 40) strength = 'fair';

    return {
      isValid: errors.length === 0,
      errors,
      strength,
      score: strengthPercentage
    };
  }, [settings]);

  // Check if password is commonly used
  const isCommonPassword = (password) => {
    const commonPasswords = [
      'password', '123456', '123456789', 'qwerty', 'abc123',
      'password123', 'admin', 'letmein', 'welcome', 'monkey',
      '1234567890', 'dragon', 'master', 'shadow', 'superman'
    ];
    return commonPasswords.includes(password.toLowerCase());
  };

  // Check password strength in real-time
  const checkPasswordStrength = useCallback((password) => {
    const result = validatePassword(password);
    setPasswordStrength(result);
    return result;
  }, [validatePassword]);

  // Terminate a session
  const terminateSession = async (sessionId) => {
    try {
      const response = await fetch(`/api/security/sessions/${sessionId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setActiveSessions(prev => prev.filter(session => session.id !== sessionId));
        return true;
      }
      return false;
    } catch (error) {
      logger.error('Failed to terminate session:', error);
      return false;
    }
  };

  // Terminate all other sessions
  const terminateAllOtherSessions = async () => {
    try {
      const response = await fetch('/api/security/sessions/terminate-others', {
        method: 'POST'
      });
      
      if (response.ok) {
        await loadActiveSessions();
        return true;
      }
      return false;
    } catch (error) {
      logger.error('Failed to terminate other sessions:', error);
      return false;
    }
  };

  // Change password with validation
  const changePassword = async (currentPassword, newPassword) => {
    // Validate new password
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      throw new Error(validation.errors.join('. '));
    }

    try {
      const response = await fetch('/api/security/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to change password');
      }

      // Force logout on other sessions if enabled
      if (settings.force_logout_on_password_change) {
        await terminateAllOtherSessions();
      }

      return true;
    } catch (error) {
      logger.error('Password change failed:', error);
      throw error;
    }
  };

  // Enable/disable 2FA
  const toggle2FA = async (enable, backupCodes = []) => {
    try {
      const response = await fetch('/api/security/2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enabled: enable,
          backupCodes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update 2FA settings');
      }

      await updateSecuritySettings({ enable_2fa: enable });
      return true;
    } catch (error) {
      logger.error('2FA toggle failed:', error);
      throw error;
    }
  };

  // Get security recommendations
  const getSecurityRecommendations = useCallback(() => {
    const recommendations = [];

    if (!settings.enable_2fa) {
      recommendations.push({
        type: 'warning',
        title: 'Enable Two-Factor Authentication',
        description: 'Add an extra layer of security to your account',
        action: 'enable_2fa'
      });
    }

    if (settings.min_password_length < 12) {
      recommendations.push({
        type: 'info',
        title: 'Increase Password Length',
        description: 'Consider requiring passwords of at least 12 characters',
        action: 'increase_password_length'
      });
    }

    if (!settings.encrypt_sensitive_data) {
      recommendations.push({
        type: 'error',
        title: 'Enable Data Encryption',
        description: 'Protect sensitive data with encryption',
        action: 'enable_encryption'
      });
    }

    if (activeSessions.length > settings.max_concurrent_sessions) {
      recommendations.push({
        type: 'warning',
        title: 'Too Many Active Sessions',
        description: 'Consider terminating unused sessions',
        action: 'manage_sessions'
      });
    }

    return recommendations;
  }, [settings, activeSessions]);

  // Format security event for display
  const formatSecurityEvent = (event) => {
    const eventTypes = {
      login: 'Login',
      logout: 'Logout',
      password_change: 'Password Changed',
      failed_login: 'Failed Login',
      session_terminated: 'Session Terminated',
      settings_changed: 'Settings Changed',
      suspicious_activity: 'Suspicious Activity'
    };

    return {
      ...event,
      typeName: eventTypes[event.type] || event.type,
      timeAgo: getTimeAgo(event.timestamp)
    };
  };

  // Get time ago string
  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const value = {
    settings,
    isLoading,
    activeSessions,
    securityEvents: securityEvents.map(formatSecurityEvent),
    passwordStrength,
    updateSecuritySettings,
    validatePassword,
    checkPasswordStrength,
    terminateSession,
    terminateAllOtherSessions,
    changePassword,
    toggle2FA,
    getSecurityRecommendations: getSecurityRecommendations(),
    loadActiveSessions,
    loadSecurityEvents
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};

export default SecurityContext;